import { Link } from "react-router-dom";
import { ArrowRight, Plane, IdCard, MapPin, TrendingUp, Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { PageHero, SectionHeading, CTABand, usePageTitle } from "../components/Shared";
import { IMAGES } from "../data/content";
import { useLang } from "../i18n";

const OFFER_ICONS = [Plane, IdCard, MapPin, TrendingUp];

const COPY = {
  fr: {
    meta: "Services pour les étrangers",
    eyebrow: "Vous êtes étranger(ère)",
    h1a: "Le Togo vous accueille.",
    h1b: "Nous gérons le reste.",
    sub: "Visiter, travailler, investir ou vous installer au Togo : DOC TOGO sécurise chacune de vos formalités, de la demande de visa à l'installation complète.",
    offersEyebrow: "Quatre situations, une équipe",
    offersTitle: "Quel que soit votre projet au Togo.",
    offers: [
      { title: "Visiter le Togo", desc: "Visa d'entrée, prolongation, sauf-conduit : nous préparons et suivons vos formalités avant et pendant votre séjour.", items: ["Visa d'entrée & prolongation", "Sauf-conduit", "Assistance à l'arrivée"] },
      { title: "Séjourner & travailler", desc: "Cartes de séjour, autorisations de travail et formalités de résidence pour vivre au Togo en toute légalité.", items: ["Carte de séjour temporaire / permanente", "Autorisations de travail", "Renouvellements & suivi"] },
      { title: "S'installer", desc: "Installation de famille ou mobilité professionnelle : nous coordonnons l'ensemble des démarches d'établissement.", items: ["Formalités d'installation", "État civil & regroupement", "Accompagnement logement-administratif"] },
      { title: "Investir & entreprendre", desc: "Création de filiale, immatriculation, conventions administratives : votre projet d'investissement entre de bonnes mains.", items: ["Création de société & filiale", "Immatriculation CFE / RCCM / NIF", "Relations avec les administrations"] },
    ],
    trustEyebrow: "Sérénité garantie",
    trustTitle: "Votre relais administratif de confiance au Togo.",
    trustSub: "Arriver dans un nouveau pays implique des dizaines de formalités. Nous les connaissons toutes — et nous les gérons pour vous.",
    trustItems: [
      "Un interlocuteur francophone et anglophone",
      "Procédures expliquées clairement, sans jargon",
      "Devis détaillé avant tout engagement",
      "Suivi en temps réel par email et WhatsApp",
      "Documents officiels vérifiés avant remise",
      "Réseau local éprouvé auprès des administrations",
    ],
    trustCta: "Préparer mon arrivée",
    trustImgAlt: "Échanges internationaux avec le Togo",
    badgeValue: "GMT+0",
    badgeLabel: "le Togo, à l'heure de l'Europe et du monde",
    ctaTitle: "Votre projet au Togo mérite un départ parfait.",
    ctaSub: "Parlez-nous de votre venue, installation ou investissement : réponse détaillée sous 24h.",
    heroImgAlt: "Lomé, Togo",
  },
  en: {
    meta: "Services for foreign nationals",
    eyebrow: "For foreign nationals",
    h1a: "Togo welcomes you.",
    h1b: "We handle the rest.",
    sub: "Visiting, working, investing or settling in Togo: DOC TOGO secures each of your formalities, from visa application to full relocation.",
    offersEyebrow: "Four situations, one team",
    offersTitle: "Whatever your project in Togo.",
    offers: [
      { title: "Visit Togo", desc: "Entry visa, extension, travel pass: we prepare and track your formalities before and during your stay.", items: ["Entry visa & extension", "Travel pass (sauf-conduit)", "Arrival assistance"] },
      { title: "Stay & work", desc: "Residence permits, work authorisations and residency formalities to live in Togo legally.", items: ["Temporary / permanent residence permit", "Work authorisations", "Renewals & follow-up"] },
      { title: "Settle in", desc: "Family relocation or professional mobility: we coordinate all settlement procedures.", items: ["Settlement formalities", "Civil status & family reunification", "Housing-administrative support"] },
      { title: "Invest & do business", desc: "Subsidiary setup, registration, administrative agreements: your investment project is in good hands.", items: ["Company & subsidiary formation", "CFE / RCCM / NIF registration", "Relations with government agencies"] },
    ],
    trustEyebrow: "Peace of mind guaranteed",
    trustTitle: "Your trusted administrative relay in Togo.",
    trustSub: "Arriving in a new country means dozens of formalities. We know them all — and we handle them for you.",
    trustItems: [
      "A French- and English-speaking contact",
      "Procedures explained clearly, without jargon",
      "Detailed quote before any commitment",
      "Real-time tracking by email and WhatsApp",
      "Official documents verified before delivery",
      "Proven local network with the authorities",
    ],
    trustCta: "Prepare my arrival",
    trustImgAlt: "International exchanges with Togo",
    badgeValue: "GMT+0",
    badgeLabel: "Togo, on Europe's time zone",
    ctaTitle: "Your project in Togo deserves a perfect start.",
    ctaSub: "Tell us about your visit, relocation or investment: detailed reply within 24h.",
    heroImgAlt: "Lomé, Togo",
  },
};

export default function Etrangers() {
  const { lang } = useLang();
  const c = COPY[lang];
  usePageTitle(c.meta);
  return (
    <div data-testid="etrangers-page">
      <PageHero
        eyebrow={c.eyebrow}
        lines={[<>{c.h1a}</>, <>{c.h1b}</>]}
        sub={c.sub}
        image={IMAGES.lome}
        imageAlt={c.heroImgAlt}
      />
      <section className="bg-ivory py-24 sm:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <SectionHeading eyebrow={c.offersEyebrow} title={c.offersTitle} />
          <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2">
            {c.offers.map((o, i) => {
              const Icon = OFFER_ICONS[i];
              return (
                <Reveal key={o.title} delay={i * 0.08}>
                  <div
                    data-testid={`etrangers-offer-${i}`}
                    className="group h-full rounded-3xl border border-brand/10 bg-white p-8 shadow-sm transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-gold/40 hover:shadow-lg"
                  >
                    <span className="grid h-12 w-12 place-items-center rounded-2xl bg-brand-light text-brand transition-colors duration-300 group-hover:bg-brand group-hover:text-white">
                      <Icon className="h-6 w-6" />
                    </span>
                    <h3 className="mt-5 font-display text-xl font-bold text-slate-900">{o.title}</h3>
                    <p className="mt-2.5 text-sm leading-relaxed text-slate-600">{o.desc}</p>
                    <ul className="mt-5 space-y-2 border-t border-slate-100 pt-5">
                      {o.items.map((item) => (
                        <li key={item} className="flex items-start gap-2 text-xs font-medium text-slate-500">
                          <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand" /> {item}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
      <section className="bg-white py-24 sm:py-28">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-2">
          <Reveal>
            <SectionHeading eyebrow={c.trustEyebrow} title={c.trustTitle} sub={c.trustSub} />
            <ul className="mt-8 grid gap-4 sm:grid-cols-2">
              {c.trustItems.map((r) => (
                <li key={r} className="flex items-start gap-3 rounded-xl border border-brand/10 bg-ivory px-4 py-3.5 text-sm font-medium text-slate-700">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand" /> {r}
                </li>
              ))}
            </ul>
            <Link
              to="/contact?categorie=Services%20%C3%A9trangers"
              data-testid="etrangers-cta-btn"
              className="group mt-9 inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 font-display font-bold text-white transition-[background-color,transform] duration-300 hover:bg-brand-hover hover:-translate-y-0.5"
            >
              {c.trustCta}
              <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2rem] border border-gold/30 rotate-2" />
              <img src={IMAGES.africa} alt={c.trustImgAlt} loading="lazy" className="relative h-80 w-full rounded-[2rem] object-cover shadow-xl lg:h-[480px]" />
              <div className="absolute -bottom-6 -right-4 rounded-2xl bg-brand-deep p-6 text-white shadow-2xl lg:-right-6">
                <div className="font-display text-3xl font-extrabold text-gold">{c.badgeValue}</div>
                <div className="mt-1 text-xs text-white/60">{c.badgeLabel}</div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
      <CTABand title={c.ctaTitle} sub={c.ctaSub} />
    </div>
  );
}
